import React, { createContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ROLES_AND_ACCOUNT_ACTIONS, GET_ADMIN_ACCOUNT } from 'src/gql/admin';
import useAuth from 'src/hooks/useAuth';
import { buildAbility } from 'src/utils/ability';

export const AbilityContext = createContext();

export const AbilityProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  const [ability, setAbility] = useState(() => {
    // Initial stale warm-up from localStorage
    try {
      const storedRolesAndActions = localStorage.getItem('sp_rolesAndActions');
      const storedUserActions = localStorage.getItem('sp_userActions');
      
      if (storedRolesAndActions && user && user.role) {
        const rolesAndActions = JSON.parse(storedRolesAndActions);
        const userActions = storedUserActions ? JSON.parse(storedUserActions) : {};
        return buildAbility(user.role, userActions, rolesAndActions);
      }
    } catch (e) {
      console.error('Error parsing localStorage for ability', e);
    }
    
    // Default fallback
    return buildAbility(user?.role || 'GUEST');
  });

  const { data: rolesData } = useQuery(GET_ROLES_AND_ACCOUNT_ACTIONS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-first'
  });

  const { data: accountData } = useQuery(GET_ADMIN_ACCOUNT, {
    variables: { accountId: user?._id },
    skip: !isAuthenticated || !user?._id || user?.role === 'SUPER_ADMIN',
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem('sp_rolesAndActions');
      localStorage.removeItem('sp_userActions');
      setAbility(buildAbility('GUEST'));
      return;
    }

    let currentRolesAndActions = null;
    let currentUserActions = {};

    try {
      const storedRolesAndActions = localStorage.getItem('sp_rolesAndActions');
      if (storedRolesAndActions) {
        currentRolesAndActions = JSON.parse(storedRolesAndActions);
      }
    } catch (e) {}

    try {
      const storedUserActions = localStorage.getItem('sp_userActions');
      if (storedUserActions) {
        currentUserActions = JSON.parse(storedUserActions);
      }
    } catch (e) {}

    let updated = false;

    if (rolesData && rolesData.getRolesAndAccountActions) {
      currentRolesAndActions = rolesData.getRolesAndAccountActions;
      localStorage.setItem('sp_rolesAndActions', JSON.stringify(currentRolesAndActions));
      updated = true;
    }

    if (accountData && accountData.getAdminAccount && accountData.getAdminAccount.account) {
      currentUserActions = accountData.getAdminAccount.account.actions || {};
      localStorage.setItem('sp_userActions', JSON.stringify(currentUserActions));
      updated = true;
    }

    if (updated || user?.role === 'SUPER_ADMIN') {
      setAbility(buildAbility(user?.role, currentUserActions, currentRolesAndActions));
    }

  }, [isAuthenticated, user, rolesData, accountData]);

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

export default AbilityContext;
